import {
	CoreButton,
	CoreFormLabel,
	CoreMenuItem,
	CoreModal,
	CoreModalDescription,
	CoreSelectTextField,
	CoreTextField,
	coreToast,
} from "@verity/components/core";
import { useContext, useEffect, useState } from "react";
import { determineAccountType } from "../../../helpers/account";
import apiClient from "../../../helpers/api-client";
import { validateEmail } from "../../../helpers/email";
import { getProduct } from "../../../helpers/url";
import i18n from "../../../i18n";
import { PortalControllerContext } from "../../context";
import styles from "./create-user-modal.module.css";
import { ROLE_CATEGORIES, RoleCategory } from "./roster-types";
import { useCreateBasicAuthUserMutation } from "./store/roster-api";

export default function CreateUserModal({ onClose }: { onClose: () => void }) {
	const { portalContext } = useContext(PortalControllerContext);
	const { lms, instituteUuid } = portalContext;
	const [createUser] = useCreateBasicAuthUserMutation();

	const [email, setEmail] = useState("");
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");

	const visibleRoleCategories: RoleCategory[] = lms
		? [...ROLE_CATEGORIES]
		: ROLE_CATEGORIES.filter((category) => category !== "STUDENT");
	const [role, setRole] = useState<RoleCategory>(lms ? "STUDENT" : "INSTRUCTOR");
	useEffect(() => {
		if (!lms && role === "STUDENT") {
			setRole("INSTRUCTOR");
		}
	}, [lms]);

	const emailEmptyError = !email.trim();
	const emailFormatError = !validateEmail(email);

	const product: "candidate" | "verity" = getProduct() === "candidate" ? "candidate" : "verity";

	return (
		<CoreModal
			open
			onClose={onClose}
			title={i18n.t("portal.components.roster.createUser.createUser")}
			buttons={
				<>
					<CoreButton color="secondary" onClick={onClose}>
						{i18n.t("portal.components.roster.createUser.cancel")}
					</CoreButton>
					<CoreButton
						onClick={async () => {
							if (instituteUuid) {
								try {
									const existingAccounts = await apiClient.authentication.getAccounts({ email });
									const accountUsers = await Promise.all(
										existingAccounts.accounts
											.filter((account) => determineAccountType(account) !== "standalone")
											.flatMap(({ userIds }) => userIds)
											.map((id) => apiClient.user.getAccessControlUserInfo(id)),
									);
									if (accountUsers.some(({ instituteId }) => instituteId === instituteUuid)) {
										coreToast.error(i18n.t("portal.components.roster.createUser.emailExists"));
										return;
									}

									const privilegeGroupIds = await apiClient.roster.getPrivilegeGroupIdsForRole(instituteUuid, role);
									const { user } = await createUser({
										instituteUuid,
										email,
										firstName,
										lastName,
										privilegeGroupIds,
									}).unwrap();
									coreToast.success(i18n.t("portal.components.roster.createUser.createSuccess", { name: user.name }));
									onClose();
								} catch (e) {
									console.error(e);
									coreToast.error(i18n.t("portal.components.roster.createUser.createFail"));
								}
							} else {
								console.error("no instituteUuid");
							}
						}}
						disabled={emailEmptyError || emailFormatError}
					>
						{i18n.t("portal.components.roster.createUser.create")}
					</CoreButton>
				</>
			}
		>
			<div className={styles.modalBody}>
				<CoreModalDescription>{i18n.t("portal.components.roster.createUser.description")}</CoreModalDescription>

				<CoreFormLabel
					labelPlacement="top"
					control={
						<CoreTextField
							required
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							placeholder={i18n.t("portal.components.roster.createUser.email")}
							error={
								emailEmptyError || emailFormatError
									? i18n.t("portal.components.roster.createUser.emailFormatError")
									: undefined
							}
						/>
					}
					header={i18n.t("portal.components.roster.createUser.email")}
				/>
				<CoreFormLabel
					labelPlacement="top"
					control={
						<CoreTextField
							value={firstName}
							onChange={(e) => setFirstName(e.target.value)}
							placeholder={i18n.t("portal.components.roster.createUser.firstName")}
						/>
					}
					header={i18n.t("portal.components.roster.createUser.firstName")}
				/>
				<CoreFormLabel
					labelPlacement="top"
					control={
						<CoreTextField
							value={lastName}
							onChange={(e) => setLastName(e.target.value)}
							placeholder={i18n.t("portal.components.roster.createUser.lastName")}
						/>
					}
					header={i18n.t("portal.components.roster.createUser.lastName")}
				/>
				<CoreFormLabel
					labelPlacement="top"
					control={
						<CoreSelectTextField<RoleCategory>
							required
							value={role}
							onChange={(e) => {
								const newRole = e.target.value;
								if (visibleRoleCategories.includes(newRole as RoleCategory)) {
									setRole(newRole as RoleCategory);
								}
							}}
							label={i18n.t("portal.components.roster.createUser.role")}
							renderValue={(value) => i18n.t(`portal.components.roster.table.row.role.${product}.${value}`)}
						>
							{visibleRoleCategories.map((role) => (
								<CoreMenuItem key={role} value={role}>
									{i18n.t(`portal.components.roster.table.row.role.${product}.${role}`)}
								</CoreMenuItem>
							))}
						</CoreSelectTextField>
					}
					header={i18n.t("portal.components.roster.createUser.role")}
				/>
			</div>
		</CoreModal>
	);
}
